import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import {
  TInputField,
  TAcquisitionState,
  TBooleanField,
} from "../../providers/ModernAcquisitionProvider/types";
import { useAcquisitionStore } from "../../providers/ModernAcquisitionProvider/useAcquisitionStore";
import { useAcquisitionContext } from "../../providers/AcquisitionProvider/AcquisitionProvider";
import { useFeatureFlags } from "../../providers/FeatureFlagProvider/FeatureFlagProvider";
import legacyReadManager from "./legacyReadManager";
import modernReadManager from "./modernReadManager";
import legacyWriteManager from "./legacyWriteManager";
import modernWriteManager from "./modernWriteManager";
import modernFormControlManager from "./modernFormControlManager";
import { AcquisitionFlow, checkIfInValidAcquisitionFlow, getAcquisitionFlow } from "../../utils/acquisition-utils";
import SessionStorage from "../../utils/SessionStorage";
import { regexValidation } from "../../utils/cob-utils";
import useBriteVerify from "../../hooks/useBriteVerify";


export type TGetFeatureFlags = (
  flagIdentifier: string,
) => boolean | { [key: string]: boolean };

const defaultInputField = (): TInputField => ({
  value: "",
  state: "neutral",
  errorType: "None",
});
const defaultBooleanField = (): TBooleanField => ({
  value: false,
  state: "neutral",
  errorType: "None",
});
const initialFields: TAcquisitionState = {
  authorizedUserDateOfBirthDay: defaultInputField(),
  authorizedUserDateOfBirthMonth: defaultInputField(),
  authorizedUserDateOfBirthYear: defaultInputField(),
  authorizedUserFirstName: defaultInputField(),
  authorizedUserLastName: defaultInputField(),
  authorizedUserMiddleInitial: defaultInputField(),
  authorizedUserSpouse: defaultBooleanField(),
  dateOfBirthDay: defaultInputField(),
  dateOfBirthMonth: defaultInputField(),
  dateOfBirthYear: defaultInputField(),
  emailAddress: defaultInputField(),
  firstName: defaultInputField(),
  homeAddressLineOne: defaultInputField(),
  homeAddressLineTwo: defaultInputField(),
  homeAddressCity: defaultInputField(),
  homeAddressState: defaultInputField(),
  homeAddressZipCode: defaultInputField(),
  lastName: defaultInputField(),
  mailingAddressLineOne: defaultInputField(),
  mailingAddressLineTwo: defaultInputField(),
  mailingAddressCity: defaultInputField(),
  mailingAddressState: defaultInputField(),
  mailingAddressZipCode: defaultInputField(),
  middleInitial: defaultInputField(),
  monthlyIncome: defaultInputField(),
  phoneNumber: defaultInputField(),
  ssn: defaultInputField(),
  suffix: defaultInputField(),
};

const useAcquisitionProviderFacade = () => {

  const acquisitionStore = useAcquisitionStore();

  const {
    validatePersonalInfo,
    personalInfoForm,
    setPersonalInfoForm,
    personalInfoErrors,
    setPersonalInfoErrors,
    authorizedUserInfo,
    setAuthorizedUserInfo,
    selectedCardValid,
    setSelectedCardValid,
    isCreditProtectionInfoValid,
    setIsCreditProtectionInfoValid,
    isPersonalInfoValid,
    setIsPersonalInfoValid,
    authorizedUserValidity,
    setAuthorizedUserValidity,
    balanceTransferSelected,
    creditProtectionInfo,
    esignVersion,
    initResponse,
    isAuthorisedUserSectionValid,
    isConsumerReportAgreed,
    isEsignAgreed,
    isEsignShowing,
    isEsignLinkValid,
    isEsignRequired,
    isPhoneAndIncomeValid,
    setIsPhoneNumberValid,
    isPhoneNumberValid,
    isSsnInfoValid,
    isTermsAgreed,
    isTermsValid,
    setPhoneAndIncome,
    phoneAndIncomeInfo,
    selectedCardId,
    ssnInfo,
    validationRef,
    isMonthlyIncomeValid,
    isMonthlyOrYearly,
    homeAddressPrimaryRef,
  } = useAcquisitionContext();

  const { getFeatureFlags } = useFeatureFlags();
  const personalInformation = useRef<TAcquisitionState>(initialFields);
  const { emailValidationApi, phoneValidationApi } = useBriteVerify();


  // Flag manager for implementing new fields
  const getModernFields = (): Array<keyof TAcquisitionState> => {
    const modernFields: Array<keyof TAcquisitionState> = [];
    if (getFeatureFlags('DPM-13102')) {
      modernFields.push(...[
        'authorizedUserFirstName',
        'authorizedUserLastName',
        'authorizedUserMiddleInitial',
        'firstName',
        'lastName',
        'middleInitial',
        'suffix',
      ] satisfies Array<keyof TAcquisitionState>);
    }
    if (getFeatureFlags('DPM-13566')) {
      modernFields.push('emailAddress');
    }
    if (getFeatureFlags('DPM-14217')) {
      modernFields.push('phoneNumber');
    }
    if (getFeatureFlags('DPM-14218')) {
      modernFields.push('authorizedUserDateOfBirthDay');
      modernFields.push('authorizedUserDateOfBirthMonth');
      modernFields.push('authorizedUserDateOfBirthYear');
      modernFields.push('dateOfBirthDay');
      modernFields.push('dateOfBirthMonth');
      modernFields.push('dateOfBirthYear');
    }
    return modernFields;
  };

  const activeModernFields = getModernFields();

  const _updateFields = () => {
    personalInformation.current = {
      ...personalInformation.current,
      ...legacyReadManager(
        personalInfoForm,
        personalInfoErrors,
        authorizedUserInfo,
        authorizedUserValidity
      ).normalize(),
      ...modernReadManager(acquisitionStore, activeModernFields).normalize(),
    };
  }

  const updateFieldsRef = useRef(_updateFields);
  updateFieldsRef.current = _updateFields;

  const personalInformationWrite = (update: TAcquisitionState) => {
    modernWriteManager(acquisitionStore, activeModernFields).write(update);
    legacyWriteManager(
      setPersonalInfoForm,
      setPersonalInfoErrors,
      setAuthorizedUserInfo,
      setAuthorizedUserValidity,
      setIsPhoneNumberValid,
      setPhoneAndIncome,
    ).write(update);
  };

  const validatePersonalInformation = useCallback(() => {
    /**
     * Include feature flag for any migrated fields 
     * that are used in the legacy implementation of validatePersonalInfo
     */
    if (activeModernFields.length < 1) {
      return validatePersonalInfo();
    }

    return modernFormControlManager(personalInformation).validatePersonalInformation();
  }, [validatePersonalInfo]);

  const getIsHomeAddressShown = useCallback(() => {
    // Modern provider only supports fields and not form control
    return personalInfoForm.homeAddressShown;
  }, [personalInfoForm]);

  const setIsHomeAddressShown = useCallback((bool: boolean) => {
    // Modern provider only supports fields and not form control
    setPersonalInfoForm(prev => ({
      ...prev,
      homeAddressShown: bool
    }));
  }, [setPersonalInfoForm]);

  /** Modern provider only supports fields and not form control */
  const getIsAuthorizedUserSelected = useCallback(() => {
    return authorizedUserInfo.IsAuthorizedUserSelected;
  }, [authorizedUserInfo]);

  const setIsAuthorizedUserSelected = useCallback((bool: boolean) => {
    setAuthorizedUserInfo(prev => ({
      ...prev,
      IsAuthorizedUserSelected: bool
    }));
  }, [setAuthorizedUserInfo]);

  const getIsSelectedCardValid = useCallback(() => {
    return selectedCardValid;
  }, [selectedCardValid]);

  const setIsSelectedCardValid = useCallback((bool: boolean) => {
    setSelectedCardValid(bool);
  }, [setSelectedCardValid]);

  const getCreditProtectionInfo = useCallback(() => {
    return creditProtectionInfo;
  }, [creditProtectionInfo]);

  const getIsCreditProtectionValid = useCallback(() => {
    return isCreditProtectionInfoValid;
  }, [isCreditProtectionInfoValid]);

  const setIsCreditProtectionValid = useCallback((bool: boolean) => {
    setIsCreditProtectionInfoValid(bool);
  }, [setIsCreditProtectionInfoValid]);

  const getIsPersonalInfoValid = useCallback(() => {
    if (activeModernFields.length < 1) {
      return isPersonalInfoValid;
    }
    if (checkIfInValidAcquisitionFlow([AcquisitionFlow.PqOfOne, AcquisitionFlow.PHA])) {
      return true;
    }
    return modernFormControlManager(personalInformation).validatePersonalInformation();
  }, [isPersonalInfoValid]);

  const setPersonalInfoValid = useCallback((bool: boolean) => {
    setIsPersonalInfoValid(bool);
  }, [setIsPersonalInfoValid]);

  const getEsign = useCallback(() => {
    return {
      isEsignAgreed,
      esignVersion,
      isEsignShowing,
      isEsignLinkValid,
      isEsignRequired,
    };
  }, [
    isEsignAgreed,
    esignVersion,
    isEsignShowing,
    isEsignLinkValid,
    isEsignRequired,
  ]);

  const getIsPhoneAndIncomeValid: () => boolean = useCallback(() => {
    if (activeModernFields.includes('phoneNumber')) {
      return personalInformation.current.phoneNumber.errorType === 'None' && personalInformation.current.phoneNumber.state !== 'error' && isMonthlyIncomeValid;
    }
    return isPhoneAndIncomeValid;
  }, [isPhoneAndIncomeValid]);

  const getIsPhoneNumberValid: () => boolean = useCallback(() => {
    if (activeModernFields.includes('phoneNumber')) {
      return personalInformation.current.phoneNumber.errorType === 'None' && personalInformation.current.phoneNumber.state !== 'error';
    }
    return isPhoneNumberValid;
  }, [isPhoneNumberValid]);

  const getPhoneAndIncomeInfo: () => Pick<TAcquisitionState, 'phoneNumber' | 'monthlyIncome'> = useCallback(() => {
    if (activeModernFields.includes('phoneNumber')) {
      return {
        phoneNumber: { value: personalInformation.current.phoneNumber.value },
        monthlyIncome: { value: phoneAndIncomeInfo.income },
      }
    }
    return {
      phoneNumber: { value: phoneAndIncomeInfo.phone },
      monthlyIncome: { value: phoneAndIncomeInfo.income },
    }
  }, [phoneAndIncomeInfo]);

  const getSelectedCardId = useCallback(() => {
    return selectedCardId;
  }, [selectedCardId]);

  const getSsn = useCallback(() => {
    return {
      isSsnInfoValid,
      ssnInfo,
    }
  }, [
    ssnInfo,
    isSsnInfoValid,
  ]);

  const getTerms = useCallback(() => {
    return {
      isTermsAgreed,
      isTermsValid,
    }
  }, [
    isTermsAgreed,
    isTermsValid,
  ]);

  const getAuthorizedUser = useCallback(() => {
    return {
      authorizedUserInfo,
      isAuthorisedUserSectionValid,
    };
  }, [
    authorizedUserInfo,
    isAuthorisedUserSectionValid,
  ]);

  const getAuthorisedUserValidity = useCallback(() => {
    return {
      authorizedUserValidity,
      setAuthorizedUserValidity,
    }
  }, [
    authorizedUserValidity,
    setAuthorizedUserValidity,
  ]);

  const getAncillarySelections = useCallback(() => {
    return {
      balanceTransferSelected,
    }
  }, [
    balanceTransferSelected,
  ]);

  const getConsumerReport = useCallback(() => {
    return {
      isConsumerReportAgreed
    };
  }, [isConsumerReportAgreed]);

  const getValidationRef = useCallback(() => {
    return validationRef;
  }, [validationRef]);

  const getPersonalInfoForm = useCallback(() => {
    return personalInfoForm;
  }, [personalInfoForm]);

  const getPersonalInfoErrors = useCallback(() => {
    return personalInfoErrors;
  }, [personalInfoErrors])

  //Facade DOB helper
  const getPersonalInformationDob = (): string => {
    let personalInformationDob = ''
    const pi = personalInformation.current;
    if (pi.dateOfBirthMonth.value || pi.dateOfBirthDay.value || pi.dateOfBirthYear.value) {
      personalInformationDob = `${pi.dateOfBirthMonth?.value ?? ''}/${pi.dateOfBirthDay?.value ?? ''}/${pi.dateOfBirthYear?.value ?? ''}`;
    }
    return personalInformationDob;
  }

  //Facade DOB helper
  const getAuthorizedUserDob = (): string => {
    let authorizedUserDob = ''
    const pi = personalInformation.current;
    if (pi.authorizedUserDateOfBirthMonth.value || pi.authorizedUserDateOfBirthDay.value || pi.authorizedUserDateOfBirthYear.value) {
      authorizedUserDob = `${pi.authorizedUserDateOfBirthMonth?.value ?? ''}/${pi.authorizedUserDateOfBirthDay?.value ?? ''}/${pi.authorizedUserDateOfBirthYear?.value ?? ''}`;
    }
    return authorizedUserDob;
  }

  /** end form control mapping from legacy */

  // Can switch to UseMemo to save redundant calls
  const getCurrentMetaAcquisitionFlowState = useCallback(() => {
    return {
      flow: getAcquisitionFlow(),
      isAuthor: ['4502', '3000'].some((entry) => window.location.port.includes(entry)),
      isAdobeMode: SessionStorage.contains('adobeMode'),
      useEmailApi: SessionStorage.contains('useEmailApi'),
      usePhoneApi: SessionStorage.contains('usePhoneApi'),
      regexValidation: regexValidation(),
      emailValidationApi: emailValidationApi,
      phoneValidationApi: phoneValidationApi,
      getFeatureFlags: getFeatureFlags,
    };
  }, [
    emailValidationApi,
    phoneValidationApi,
    getFeatureFlags,
  ]);

  const getInitResponse = useCallback(() => {
    return initResponse;
  }, [initResponse]);

  const getMonthlyOrYearlyIncome = useCallback(() => {
    return {
      isMonthlyIncomeValid,
      isMonthlyOrYearly
    }
  }, [
    isMonthlyIncomeValid,
    isMonthlyOrYearly
  ]);

  const getHomeAddressPrimaryRef = useCallback(() => {
    return homeAddressPrimaryRef;
  }, [homeAddressPrimaryRef])

  // ensure facade is subscribed before any provider writes
  // specifically an issue on prefill of writes on mount
  useLayoutEffect(() => {
    const syncPersonalInformation = () => {
      updateFieldsRef.current();
    };

    return acquisitionStore.subscribe(syncPersonalInformation);
  }, []);

  useEffect(() => {
    _updateFields();
  }, [
    personalInfoForm,
    authorizedUserInfo,
    authorizedUserValidity,
    creditProtectionInfo,
    isEsignAgreed,
    esignVersion,
    isEsignShowing,
    isTermsAgreed,
    isConsumerReportAgreed,
    balanceTransferSelected,
  ]);

  return {
    // ensure up to date values are always pulled when requested
    getPersonalInformation: () => personalInformation.current,
    personalInformationWrite,
    validatePersonalInformation,
    getIsHomeAddressShown,
    setIsHomeAddressShown,
    getIsAuthorizedUserSelected,
    setIsAuthorizedUserSelected,
    getIsSelectedCardValid,
    setIsSelectedCardValid,
    getIsCreditProtectionValid,
    setIsCreditProtectionValid,
    getIsPersonalInfoValid,
    setPersonalInfoValid,
    getEsign,
    getIsPhoneAndIncomeValid,
    getIsPhoneNumberValid,
    getPhoneAndIncomeInfo,
    getSelectedCardId,
    getSsn,
    getTerms,
    getAuthorizedUser,
    getConsumerReport,
    getValidationRef,
    getPersonalInfoForm,
    getCurrentMetaAcquisitionFlowState,
    getInitResponse,
    getCreditProtectionInfo,
    getMonthlyOrYearlyIncome,
    getPersonalInfoErrors,
    getAncillarySelections,
    getHomeAddressPrimaryRef,
    getModernFields,
    getPersonalInformationDob,
    getAuthorizedUserDob,
    getAuthorisedUserValidity,
  };
};

export default useAcquisitionProviderFacade;
export type TAcquisitionProviderFacade = ReturnType<typeof useAcquisitionProviderFacade>;
